// Adapter Registry — resolves the active product line and instantiates the
// correct Auth + BFF adapters + a config-driven CanvasResolver from declarative config.
//
// Pre-registered types:
//   Auth: 'service-auth'   → ServiceAuthAdapter
//   BFF:  'rest', 'esa-rest' → RestBffAdapter; 'graphql' → GraphqlBffAdapter
//
// Canvas resolution uses a single BffCanvasResolver configured with path
// templates + dot-path field extractors (no per-product-line resolver types).

import type { AuthFlow } from './auth/AuthAdapter';
import type { AuthAdapter } from './auth/AuthAdapter';
import type { BffAdapter } from './bff/BffAdapter';
import { type CanvasResolver, type CanvasResolverConfig, BffCanvasResolver } from './canvas/CanvasResolver';
import type { ClaimMapping } from './auth/claimResolver';
import type { AuthEndpoints } from './auth/ServiceAuthAdapter';
import { ServiceAuthAdapter, type ServiceAuthConfig } from './auth/ServiceAuthAdapter';
import { RestBffAdapter, type RestBffConfig } from './bff/RestBffAdapter';
import { GraphqlBffAdapter, type GraphqlBffConfig } from './bff/GraphqlBffAdapter';
import { HOST_ADAPTERS_WINDOW_KEY } from '../constants/storageKeys';

export interface AuthAdapterConfig {
  type: string;
  serviceUrl: string;
  endpoints: AuthEndpoints;
  flow?: AuthFlow;
  tenant?: string;
  claimMapping?: ClaimMapping;
  [key: string]: unknown;
}

export interface BffAdapterConfig {
  type: string;
  baseUrl: string;
  /** Optional DESO origin for converse / status SSE / style-preferences. */
  converseUrl?: string;
  tenant?: string;
  tenantHeader?: string;
  authHeader?: string;
  authScheme?: string;
  defaultHeaders?: Record<string, string>;
  /** Header name for propagating user identity (user_ukey). Defaults to 'x-user-id'. */
  userHeader?: string;
  [key: string]: unknown;
}

/**
 * Post-login shell hydration strategy (config-driven — no product-line id checks).
 * Default when omitted: ESA experience endpoints.
 */
export type HydrationConfig =
  | { type: 'experience' }
  | {
      type: 'bootstrap';
      /** Bootstrap path without query (default `/app/bootstrap`). */
      path?: string;
      /** Optional `surface` query param — appended only when set. */
      surface?: string;
      /** Workspace ids granted in the mapped PermissionsManifest (default `['operational']`). */
      grantWorkspaces?: string[];
      /**
       * Used when bootstrap returns empty navigation (e.g. `{ nodes: [] }`).
       * Local/dev only — remove once the gateway seeds real nodes.
       */
      mockNavigation?: unknown[];
    };


export interface ProductLineFeatures {
  /**
   * ESA SSE notification stream (`/notifications/stream`).
   * Default `true` when omitted; set `false` for product lines that do not expose it.
   */
  notificationStream?: boolean;
  /**
   * Global header waffle (module/solution picker).
   * Default `true` when omitted; set `false` for product lines that do not use it (e.g. CMS).
   */
  waffleMenu?: boolean;
  /**
   * Omnipresent ESA side panel (chat, inbox, briefing).
   * Default `true` when omitted; set `false` for product lines that do not use it.
   */
  esaPanel?: boolean;
  /**
   * Branded module header (EDB brand mark + active module/solution label).
   * Default `true` when omitted; set `false` for product lines with their own branding.
   */
  brandedModuleHeader?: boolean;
}

export interface ProductLineDeclaration {
  id: string;
  label: string;
  auth: AuthAdapterConfig;
  bff: BffAdapterConfig;
  hydration?: HydrationConfig;
  canvas?: CanvasResolverConfig;
  /**
   * Post-login canvas/module ukey (navigates to `/operational/canvas/{ukey}`).
   * When omitted, host uses the code default landing path.
   */
  landingCanvasUkey?: string;
  features?: ProductLineFeatures;
  hostnames?: string[];
}

type AuthFactory = (cfg: AuthAdapterConfig) => AuthAdapter;
type BffFactory = (cfg: BffAdapterConfig, auth: AuthAdapter) => BffAdapter;

const authFactories = new Map<string, AuthFactory>();
const bffFactories = new Map<string, BffFactory>();

export function registerAuthFactory(type: string, factory: AuthFactory): void {
  authFactories.set(type, factory);
}

export function registerBffFactory(type: string, factory: BffFactory): void {
  bffFactories.set(type, factory);
}

registerAuthFactory('service-auth', cfg => {
  const c = cfg as unknown as ServiceAuthConfig;
  return new ServiceAuthAdapter(c);
});

registerBffFactory('rest', (cfg, auth) => {
  const c = cfg as unknown as RestBffConfig;
  return new RestBffAdapter(c, auth);
});

registerBffFactory('esa-rest', (cfg, auth) => {
  const c = cfg as unknown as RestBffConfig;
  return new RestBffAdapter(c, auth);
});

registerBffFactory('graphql', (cfg, auth) => {
  const c = cfg as unknown as GraphqlBffConfig;
  return new GraphqlBffAdapter(c, auth);
});

let activeAuth: AuthAdapter | null = null;
let activeBff: BffAdapter | null = null;
let activeCanvasResolver: CanvasResolver | null = null;
let activeProductLineId: string | null = null;
let productLineDeclarations: ProductLineDeclaration[] = [];

/** Cross-chunk singleton — MF expose chunks (e.g. CanvasEngine) may bundle a duplicate module. */
const ADAPTER_GLOBAL_KEY = HOST_ADAPTERS_WINDOW_KEY;

interface AdapterGlobal {
  auth: AuthAdapter;
  bff: BffAdapter;
  canvasResolver: CanvasResolver;
  productLineId: string;
  /** Full declarations so expose chunks can read hydration / canvas config. */
  productLines: ProductLineDeclaration[];
}

function readAdapterGlobal(): AdapterGlobal | null {
  const g = (globalThis as Record<string, unknown>)[ADAPTER_GLOBAL_KEY];
  return g && typeof g === 'object' ? (g as AdapterGlobal) : null;
}

function writeAdapterGlobal(value: AdapterGlobal): void {
  (globalThis as Record<string, unknown>)[ADAPTER_GLOBAL_KEY] = value;
}

export function getAuthAdapter(): AuthAdapter {
  const globalAdapters = readAdapterGlobal();
  if (globalAdapters?.auth) return globalAdapters.auth;
  if (!activeAuth) throw new Error('Auth adapter not initialised — call initAdapters() first');
  return activeAuth;
}

export function getBffAdapter(): BffAdapter {
  const globalAdapters = readAdapterGlobal();
  if (globalAdapters?.bff) return globalAdapters.bff;
  if (!activeBff) throw new Error('BFF adapter not initialised — call initAdapters() first');
  return activeBff;
}

export function getCanvasResolver(): CanvasResolver {
  const globalAdapters = readAdapterGlobal();
  if (globalAdapters?.canvasResolver) return globalAdapters.canvasResolver;
  if (!activeCanvasResolver) throw new Error('Canvas resolver not initialised — call initAdapters() first');
  return activeCanvasResolver;
}

export function getProductLineDeclarations(): ProductLineDeclaration[] {
  const globalAdapters = readAdapterGlobal();
  if (globalAdapters?.productLines?.length) return globalAdapters.productLines;
  return productLineDeclarations;
}

export function getActiveProductLineId(): string | null {
  const globalAdapters = readAdapterGlobal();
  if (globalAdapters?.productLineId) return globalAdapters.productLineId;
  return activeProductLineId;
}

/** Active product-line declaration (auth/bff/hydration/canvasRegistry). */
export function getActiveProductLine(): ProductLineDeclaration | null {
  const id = getActiveProductLineId();
  if (!id) return null;
  return getProductLineDeclarations().find(d => d.id === id) ?? null;
}

function resolveProductLine(
  declarations: ProductLineDeclaration[],
  defaultId?: string
): ProductLineDeclaration {
  const host = window.location.hostname;
  const byHost = declarations.find(d => d.hostnames?.includes(host));
  if (byHost) return byHost;

  if (defaultId) {
    const byDefault = declarations.find(d => d.id === defaultId);
    if (byDefault) return byDefault;
  }

  const fallback = declarations[0];
  if (!fallback) {
    throw new Error(
      '[adapters] No product-line declarations available. ' +
        'Set UI_PRODUCT_LINES in public/envConfig.js, or set UI_PKS_URL ' +
        'to point to a running gaxl-platform-knowledge-service instance.'
    );
  }
  return fallback;
}

export interface AdapterInitConfig {
  productLines: ProductLineDeclaration[];
  defaultProductLineId?: string;
}

export function initAdapters(cfg: AdapterInitConfig): { auth: AuthAdapter; bff: BffAdapter; canvasResolver: CanvasResolver } {
  productLineDeclarations = cfg.productLines;
  const pl = resolveProductLine(cfg.productLines, cfg.defaultProductLineId);
  activeProductLineId = pl.id;

  const authFactory = authFactories.get(pl.auth.type);
  if (!authFactory) {
    throw new Error(
      `No auth adapter registered for type "${pl.auth.type}". Registered: ${[...authFactories.keys()].join(', ')}`
    );
  }
  activeAuth = authFactory(pl.auth);

  const bffFactory = bffFactories.get(pl.bff.type);
  if (!bffFactory) {
    throw new Error(
      `No BFF adapter registered for type "${pl.bff.type}". Registered: ${[...bffFactories.keys()].join(', ')}`
    );
  }
  activeBff = bffFactory(pl.bff, activeAuth);

  activeCanvasResolver = new BffCanvasResolver(activeBff, pl.canvas ?? {});

  writeAdapterGlobal({
    auth: activeAuth,
    bff: activeBff,
    canvasResolver: activeCanvasResolver,
    productLineId: pl.id,
    productLines: cfg.productLines,
  });

  if (window.RUNTIME_CONFIG?.UI_ENV !== 'production') {
    console.info(
      '[adapters] product-line=%s  auth=%s(%s)  bff=%s(%s)  canvas=%s',
      pl.id,
      activeAuth.name,
      pl.auth.type,
      activeBff.name,
      pl.bff.type,
      activeCanvasResolver.name
    );
  }

  return { auth: activeAuth, bff: activeBff, canvasResolver: activeCanvasResolver };
}
